import isArray from 'lodash/isArray';
import qs, { ParsedQs } from 'qs';
import queryString from 'query-string';
import { API_URL } from 'src/config';
import { ROUTE_CAMPAIGN_SETUP_BASE } from 'src/routes';

import { customHistory } from 'src/utils/customHistory';
import { SafeAnyType } from 'src/utils/safeAny';

/** get search params from url */
export function getSearchParams(): ParsedQs;
export function getSearchParams(x: string): string | null;
export function getSearchParams(x: string[]): (string | null)[];
export function getSearchParams(param?: string | string[]) {
  const searchParams = qs.parse(customHistory.location.search.replace('?', ''));

  if (!param) {
    return searchParams;
  }

  const getValueByParam = (v: string) => searchParams[v] || null;

  return isArray(param) ? param.map(getValueByParam) : getValueByParam(param);
}

export const getSearchReturnUrl = (): string | null => {
  const urlParsed = queryString.parse(customHistory.location.search);
  const url = urlParsed.ReturnUrl || urlParsed.returnUrl || urlParsed.returnurl; // all cases are appear
  return Array.isArray(url) ? url[0] : url;
};

export const addReturnUrl = (url: string, returnUrl = '/'): string =>
  `${url}?ReturnUrl=${encodeURIComponent(returnUrl)}`;

export const getUrlFilters = (path: string, obj: Record<string, SafeAnyType>[]): string =>
  `${path}?${qs.stringify({
    Filters: obj,
  })}`;

/**
 * clear all url (get, query) params
 * or the only given as string array (no matter what their values are, just their names)
 */
export const clearSearchParams = (params?: string | string[], except?: string | string[]): void => {
  if (except) {
    const queryParams = getSearchParams();

    const params = (isArray(except) ? except : [except]).reduce<ParsedQs>((acc, paramToStay) => {
      if (queryParams[paramToStay]) {
        acc[paramToStay] = queryParams[paramToStay];
      }

      return acc;
    }, {});

    customHistory.replace({
      ...customHistory.location,
      search: qs.stringify(params),
    });
  } else if (params) {
    const queryParams = getSearchParams();

    (isArray(params) ? params : [params]).forEach((paramToDelete) => {
      delete queryParams[paramToDelete];
    });

    customHistory.replace({
      ...customHistory.location,
      search: qs.stringify(queryParams),
    });
  } else {
    customHistory.replace(customHistory.location.pathname);
  }
};

/**
 * set params to url (get, query params)
 */
export const setSearchParam = <T = Record<string, string | number | null | undefined | SafeAnyType[]>>(
  params: T,
  clearAll = false,
  isPush = false
): void => {
  if (clearAll) {
    clearSearchParams();
  }

  const preparedParams = {
    ...customHistory.location,
    search: qs.stringify(clearAll ? params : { ...getSearchParams(), ...params }),
  };

  if (isPush) {
    customHistory.push(preparedParams);
  } else {
    customHistory.replace(preparedParams);
  }
};

/** add a schema if it doesn't set */
export const normalizeSchemaUrl = (url: string): string => (/^(https?:\/\/)/.test(url) ? url : `https://${url}`);

export const isAbsoluteUrl = (url: string): boolean => /^https?:\/\//i.test(url);

export const getBaseDomainFromUrl = (url: string): string => {
  const regExpExecArray = /^https?:\/\/[^#?/]+/.exec(url);

  if (regExpExecArray) {
    return regExpExecArray[0];
  }
  return url;
};

export const getOtherThanBaseDomainFromUrl = (url: string): string => {
  const regExpExecArray = /^https?:\/\/[^#?/]+(.+)/.exec(url);

  return regExpExecArray ? regExpExecArray[1] : url;
};

export const isDomainInTheLinkTheSameAsTheApp = (url: string): boolean => getBaseDomainFromUrl(url) === API_URL;

/** opening new window functionality */
type WindowHackIdType = Window['openHackRef'][0]['id'];

export const prepareOpenNewWindow = (id: WindowHackIdType): void => {
  window.openHackRef.push({ id, fn: window.open() });
};

const getWindowHackFnById = (id: WindowHackIdType): Window | null => {
  const found = window.openHackRef.find((item) => item.id === id); // find fn by id

  window.openHackRef = window.openHackRef.filter((item) => item.id !== id); // remove fn by id

  return found?.fn || null;
};

export const openNewWindow = (id: WindowHackIdType, url: string): void => {
  const fn = getWindowHackFnById(id);

  if (fn) {
    fn.location = url;
  }
};

export const closeNewWindow = (id: WindowHackIdType): void => {
  getWindowHackFnById(id)?.close();
};
/** end opening new window functionality */

export const createLinkToCampaignSetupByPermanentUrl = (permanentUrl: string | number) =>
  `${ROUTE_CAMPAIGN_SETUP_BASE}/${permanentUrl}`;
